import React, { useMemo, useState } from 'react';
import type { ProvNode } from '../../lib/a2aApi';
import './a2a.css';
import { useTranslation } from 'react-i18next';

const W = 176; const H = 50; const GX = 46; const GY = 16;
const KIND_COLOR: Record<string, string> = { 'delegation.mint': '#3b82f6', 'delegation.use': '#10b981', 'delegation.revoke': '#ef4444', 'delegation.reject': '#ef4444', 'topic.review': '#f59e0b' };

/** Ancestor DAG: the queried event on the right, its causal parents to the left. */
const ProvenanceDag: React.FC<{ root: string; nodes: ProvNode[]; intact: boolean; nameOf?: (id: string) => string }> = ({ root, nodes, intact, nameOf }) => {
  const { t } = useTranslation();
  const [sel, setSel] = useState<ProvNode | null>(null);
  const { pos, width, height } = useMemo(() => {
    const byHash = new Map(nodes.map((n) => [n.hash, n]));
    const depth = new Map<string, number>([[root, 0]]);
    const queue = [root];
    while (queue.length) { const h = queue.shift()!; for (const p of byHash.get(h)?.parents || []) { const d = (depth.get(h) || 0) + 1; if ((depth.get(p) ?? -1) < d && d <= nodes.length) { depth.set(p, d); queue.push(p); } } }
    const maxD = Math.max(0, ...Array.from(depth.values()));
    const cols = new Map<number, ProvNode[]>();
    nodes.forEach((n) => { const c = maxD - (depth.get(n.hash) ?? 0); cols.set(c, [...(cols.get(c) || []), n]); });
    cols.forEach((arr) => arr.sort((a, b) => a.ts.localeCompare(b.ts)));
    const pos = new Map<string, { x: number; y: number }>(); let rows = 1;
    cols.forEach((arr, c) => { rows = Math.max(rows, arr.length); arr.forEach((n, i) => pos.set(n.hash, { x: 12 + c * (W + GX), y: 12 + i * (H + GY) })); });
    return { pos, width: 24 + (maxD + 1) * W + maxD * GX, height: 24 + rows * H + (rows - 1) * GY };
  }, [nodes, root]);

  return (
    <div>
      <div className={`a2a-banner ${intact ? 'ok' : 'err'}`}>{intact ? `Provenance intact — ${nodes.length} events, every hash re-verified back to the origin.` : t('components.a2a.provenanceDag.provenanceBROKENAtLeastOneAncestor')}</div>
      <div style={{ overflowX: 'auto' }}>
        <svg className="a2a-graph" width={Math.max(width, 420)} height={Math.max(height, 90)} role="img" aria-label={t('components.a2a.provenanceDag.auditProvenanceDAG')}>
          {nodes.flatMap((n) => n.parents.map((p) => {
            const a = pos.get(p); const b = pos.get(n.hash); if (!a || !b) return null;
            return <path key={`${p}>${n.hash}`} className="a2a-edge" d={`M${a.x + W},${a.y + H / 2} C${a.x + W + GX / 2},${a.y + H / 2} ${b.x - GX / 2},${b.y + H / 2} ${b.x},${b.y + H / 2}`} />;
          }))}
          {nodes.map((n) => {
            const p = pos.get(n.hash); if (!p) return null;
            const c = n.verified ? KIND_COLOR[n.kind] || 'var(--border-strong, #888)' : '#ef4444';
            return (
              <g key={n.hash} className="a2a-node" transform={`translate(${p.x},${p.y})`} onClick={() => setSel(n)} style={{ cursor: 'pointer' }}>
                <rect width={W} height={H} rx={8} style={{ stroke: c, strokeWidth: n.hash === root ? 3 : 1.4 }} />
                <text x={9} y={19} fontWeight={600}>{n.kind}</text>
                <text className="sub" x={9} y={35}>{(n.agentId ? (nameOf ? nameOf(n.agentId) : n.agentId) : n.principal) || '—'}{n.hop != null ? ` · hop ${n.hop}` : ''} {n.verified ? '✔' : '✘'}</text>
              </g>
            );
          })}
        </svg>
      </div>
      {sel && (
        <pre className="a2a-mono" style={{ marginTop: 8, whiteSpace: 'pre-wrap', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
{JSON.stringify({ hash: sel.hash, kind: sel.kind, status: sel.status, ts: sel.ts, agent: sel.agentId, principal: sel.principal, hop: sel.hop, parents: sel.parents, detail: sel.detail }, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default ProvenanceDag;
